import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { useEffect } from "react";
import pc from "@/assets/3d/pc.webp";
import laptop from "@/assets/3d/laptop.webp";
import wrench from "@/assets/3d/wrench.webp";
import camera from "@/assets/3d/camera.webp";
import router from "@/assets/3d/router.webp";

const objects = [
  { src: pc, alt: "PC gamer sur mesure", cls: "left-[-6%] top-[9%] w-24 md:left-[2%] md:top-[22%] md:w-52", depth: 40, scroll: -160, rot: -8, mobile: true },
  { src: laptop, alt: "Ordinateur portable avec site web", cls: "right-[-6%] top-[8%] w-24 md:right-[2%] md:top-[18%] md:w-56", depth: 55, scroll: -220, rot: 8, mobile: true },
  { src: wrench, alt: "Outils de dépannage", cls: "hidden md:block left-[12%] bottom-[2%] w-36", depth: 30, scroll: -90, rot: 14 },
  { src: camera, alt: "Caméra de vidéosurveillance", cls: "hidden md:block right-[13%] bottom-[0%] w-36", depth: 35, scroll: -120, rot: -10 },
  { src: router, alt: "Routeur WiFi", cls: "hidden lg:block left-[24%] top-[6%] w-24", depth: 20, scroll: -60, rot: 6 },
];

const Item = ({ o, i, mx, my, sy }: { o: (typeof objects)[number]; i: number; mx: ReturnType<typeof useMotionValue<number>>; my: ReturnType<typeof useMotionValue<number>>; sy: ReturnType<typeof useScroll>["scrollY"] }) => {
  const x = useSpring(useTransform(mx, [-1, 1], [-o.depth, o.depth]), { stiffness: 60, damping: 15 });
  const yMouse = useTransform(my, [-1, 1], [-o.depth, o.depth]);
  const yScroll = useTransform(sy, [0, 800], [0, o.scroll]);
  const y = useSpring(useTransform([yMouse, yScroll], ([a, b]) => (a as number) + (b as number)), { stiffness: 60, damping: 15 });
  const rotate = useTransform(sy, [0, 800], [o.rot, o.rot * -2]);
  return (
    <motion.div
      className={`absolute ${o.cls}`}
      style={{ x, y, rotate }}
      initial={{ opacity: 0, scale: 0.4 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3 + i * 0.1, type: "spring", stiffness: 120, damping: 12 }}
    >
      <img
        src={o.src}
        alt={o.alt}
        width={480}
        height={480}
        fetchPriority={i < 2 ? "high" : "auto"}
        className="w-full h-auto tile-bob drop-shadow-[0_30px_30px_hsl(240_10%_6%/0.22)] select-none"
        style={{ animationDelay: `${i * 0.5}s` }}
        draggable={false}
      />
    </motion.div>
  );
};

const HeroObjects = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollY } = useScroll();

  useEffect(() => {
    if (!window.matchMedia("(hover: hover)").matches) return;
    const onMove = (e: PointerEvent) => {
      mx.set((e.clientX / window.innerWidth) * 2 - 1);
      my.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [mx, my]);

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {objects.map((o, i) => <Item key={o.alt} o={o} i={i} mx={mx} my={my} sy={scrollY} />)}
    </div>
  );
};

export default HeroObjects;
